import { PageHeader } from "@/pages/Categories";
import { Link } from "react-router-dom";
import { ArrowUpRight, Users, Network, Gem, Sparkles, Globe2, ShieldCheck, Linkedin, Building2 } from "lucide-react";

const PILLARS = [
  {
    icon: Users,
    eyebrow: "REAL PEOPLE",
    t: "Real people. Real experience.",
    p: [
      "Our founders and partners bring over 60 years of combined business experience across international trade, sourcing, technology, manufacturing and commercial operations.",
      "We are not a directory. We are not a lead-generation website.",
      "We are experienced professionals leveraging decades of relationships and industry connections to create opportunities for buyers and suppliers worldwide.",
    ],
  },
  {
    icon: Network,
    eyebrow: "DIRECT ACCESS",
    t: "Direct access to suppliers.",
    p: ["Our goal is simple:"],
    list: ["Reduce costs", "Increase transparency", "Accelerate trade"],
    foot:
      "By working directly with manufacturers and trusted suppliers wherever possible, we help businesses improve margins and reduce sourcing complexity.",
  },
  {
    icon: Gem,
    eyebrow: "INDUSTRIAL → BESPOKE",
    t: "From industrial goods to bespoke luxury.",
    p: [
      "Our network supports sourcing and trade opportunities across multiple sectors including manufacturing, industrial products, fragrance ingredients, packaging, consumer products, precious metals and custom jewellery.",
      "From large-scale commercial orders to bespoke luxury creations, we help bring ideas and opportunities together.",
    ],
  },
  {
    icon: Globe2,
    eyebrow: "GLOBAL · LOCAL",
    t: "Global reach. Local understanding.",
    p: ["VYR Trade connects businesses across:"],
    list: ["India", "Europe", "United Kingdom", "Middle East", "Asia"],
    foot:
      "Understanding local business culture is often the difference between success and failure in international trade. Our experience helps businesses navigate those differences with confidence.",
  },
  {
    icon: Sparkles,
    eyebrow: "RELATIONSHIPS",
    t: "Built on relationships, not databases.",
    p: [
      "Many platforms rely entirely on listings. We rely on relationships.",
      "Our network has been built over decades through business partnerships, industry events, trade missions and personal introductions — opening doors that are often unavailable through traditional marketplaces.",
    ],
  },
  {
    icon: ShieldCheck,
    eyebrow: "BUILT ON TRUST",
    t: "Your success is our reputation.",
    p: [
      "Long-term business relationships are more valuable than short-term transactions.",
      "Every introduction, supplier relationship and opportunity is approached with professionalism, transparency and accountability.",
      "We are confident in the strength of our network. If we cannot create value for you, we don't deserve your business.",
    ],
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHeader
        eyebrow="ABOUT VYR TRADE"
        title="Global trade. Trusted relationships."
        sub="VYR Trade connects businesses across India, Europe, the Middle East and Asia through a trusted network of manufacturers, suppliers, traders and industry professionals — without unnecessary middlemen."
      />

      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-20 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-white/5 bg-zinc-900/40 rounded-lg p-8">
          <div className="font-mono text-[11px] tracking-[0.22em] text-amber-400">OUR STORY</div>
          <h2 className="mt-2 font-display text-2xl font-semibold">Operations across continents.</h2>
          <p className="mt-4 text-zinc-300 text-[14.5px] leading-relaxed">
            VYR Trade connects businesses across India, Europe, the Middle East and Asia through a trusted network of manufacturers, suppliers, traders and industry professionals. Whether you&apos;re sourcing industrial products, specialty materials, consumer goods, precious metals or custom-designed jewellery — we help you find the right partners without unnecessary middlemen.
          </p>
          <p className="mt-3 text-zinc-400 text-[14px] leading-relaxed">
            Our founders and partners bring 60+ years of combined business experience across international trade, sourcing, technology, manufacturing and commercial operations. Every engagement is documented, scoped and accountable — long-term relationships matter more than short-term transactions.
          </p>
        </div>

        <div className="border border-white/5 bg-zinc-950 rounded-lg p-6">
          <div className="font-mono text-[11px] tracking-[0.22em] text-amber-400">QUICK FACTS</div>
          <ul className="mt-4 grid gap-3">
            {[
              ["Combined experience", "60+ years"],
              ["Regions covered", "India · EU · UK · MEA · Asia"],
              ["Sectors", "Industrial · Specialty · Luxury"],
              ["Engagement model", "Buyer & supplier side"],
              ["Currencies", "GBP · EUR · USD · AED"],
              ["First response", "Within 24 hours"],
            ].map(([k, v]) => (
              <li key={k} className="flex items-center justify-between border-b border-white/5 pb-2.5 last:border-b-0">
                <span className="text-[12.5px] text-zinc-500 font-mono tracking-wide">{k}</span>
                <span className="text-[13px] text-zinc-200">{v}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* WHY VYR — Long-form */}
      <section className="border-y border-white/5 bg-zinc-900/30" data-testid="about-why-vyr">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 sm:py-24">
          <div className="max-w-3xl">
            <div className="font-mono text-[11px] tracking-[0.22em] text-amber-400">WHY WORK WITH VYR TRADE</div>
            <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl leading-tight tracking-tight">
              Global trade. Trusted relationships. Real opportunities.
            </h2>
            <p className="mt-4 text-zinc-400 text-[15px] leading-relaxed">
              Six principles that define how we work — and why buyers and suppliers across India, Europe, the Middle East and Asia choose VYR Trade over directory-style platforms.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-3">
            {PILLARS.map((p, i) => (
              <article
                key={p.eyebrow}
                className={`relative border border-white/5 bg-zinc-950 rounded-lg p-7 hover:border-white/15 transition-colors ${
                  i === PILLARS.length - 1 && PILLARS.length % 2 === 1 ? "md:col-span-2" : ""
                }`}
                data-testid={`about-pillar-${i}`}
              >
                <div className="flex items-center justify-between">
                  <div className="h-10 w-10 rounded-md border border-white/10 grid place-items-center bg-zinc-900">
                    <p.icon className="h-4 w-4 text-amber-400" />
                  </div>
                  <div className="font-mono text-[10px] tracking-[0.22em] text-zinc-500">
                    {p.eyebrow} · {String(i + 1).padStart(2, "0")}
                  </div>
                </div>
                <h3 className="mt-5 font-display text-[19px] font-semibold leading-tight">{p.t}</h3>
                <div className="mt-3 space-y-3">
                  {p.p.map((para, j) => (
                    <p key={j} className="text-[13.5px] text-zinc-400 leading-relaxed">{para}</p>
                  ))}
                </div>
                {p.list && (
                  <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                    {p.list.map((item) => (
                      <li key={item} className="flex items-center gap-2 text-[13px] text-zinc-300">
                        <span className="h-1 w-1 rounded-full bg-amber-400" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
                {p.foot && (
                  <p className="mt-4 text-[13px] text-zinc-300 leading-relaxed border-t border-white/5 pt-3">{p.foot}</p>
                )}
              </article>
            ))}
          </div>

          <div className="mt-12 flex justify-center">
            <Link
              to="/quote"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-amber-400 text-zinc-950 hover:bg-amber-300 font-semibold text-sm transition-colors"
              data-testid="about-why-cta"
            >
              Start a sourcing brief <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-24">
        <div className="font-mono text-[11px] tracking-[0.22em] text-amber-400">FOUNDERS</div>
        <h2 className="mt-2 font-display text-3xl font-bold">The team behind VYR Trade.</h2>
        <div className="mt-8 grid md:grid-cols-2 gap-3">
          <FounderCard initials="US" name="Umesh Sridhar" role="Co-Founder" location="London, UK" />
          <FounderCard initials="AM" name="Adithya Menon" role="Co-Founder" location="Hyderabad, India" />
        </div>

        <div className="mt-12 flex justify-center">
          <Link to="/quote" className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium" data-testid="about-cta-quote">
            Work with us <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}

function FounderCard({ initials, name, role, location }) {
  return (
    <div className="border border-white/5 bg-zinc-900/40 rounded-lg p-6 flex gap-5" data-testid={`about-founder-${initials.toLowerCase()}`}>
      <div className="h-16 w-16 rounded-md bg-gradient-to-br from-blue-600/40 to-amber-400/20 grid place-items-center border border-white/10 shrink-0">
        <Building2 className="h-6 w-6 text-amber-300/80" />
      </div>
      <div className="flex-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-display font-semibold text-lg">{name}</div>
            <div className="font-mono text-[12px] text-zinc-400 tracking-wide">{role}</div>
            <div className="text-[12px] text-amber-400/90 mt-1">{location}</div>
          </div>
          <a
            href="#"
            onClick={(e) => e.preventDefault()}
            className="h-8 w-8 rounded-md border border-white/10 grid place-items-center text-zinc-500 hover:text-amber-300 hover:border-amber-400/40 transition-colors"
            aria-label={`LinkedIn — ${name} (coming soon)`}
            title="LinkedIn profile coming soon"
            data-testid={`about-founder-${initials.toLowerCase()}-linkedin`}
          >
            <Linkedin className="h-3.5 w-3.5" />
          </a>
        </div>
        <div className="mt-4 inline-flex items-center gap-2 border border-white/5 bg-zinc-950 rounded-md px-3 py-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse-dot" />
          <span className="font-mono text-[10.5px] tracking-[0.22em] text-zinc-300">PROFILE COMING SOON</span>
        </div>
      </div>
    </div>
  );
}
